import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
  EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode,
  EnumOrganizationAppSettingsWidgetsOperatorOperatorWidgetFilter,
  EnumOwnerType,
  OrganizationAppSettingsWidgetsDocument,
  SortFindManyWidgetInput,
  useOrganizationAppSettingsWidgetsQuery,
  useOrganizationUpdateMutation,
  useWidgetManyQuery,
  WidgetManyDocument,
} from "gql/generated";
import { useLoggedInAs } from "shared/hooks";
import { useToastContext } from "@ttc3k/trekker";
import { useFeatureFlag } from "shared/contextProviders/featureFlags/useFeatureFlag";
import { FeatureFlags } from "shared/featureFlags";
import { getFilteredWidgets } from "apps/widgets/getFilteredWidgets";
import { WidgetKey } from "apps/widgets/WidgetAppView.config";
import { useOrgHome } from "apps/home/hooks";
import {
  TierDisplayOverridesByTag,
  TierDisplayOverride,
} from "./TieredSortOrder";

// Fallback: parse organizationId from pathname (e.g. /app/organization/68.../widgets) when useParams doesn't have it
function getOrganizationIdFromPathname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const match = window.location.pathname.match(
    /\/app\/organization\/([^/]+)(?:\/|$)/,
  );
  return match?.[1];
}

const defaultOperatorSettings = {
  hideTemporarilyClosed: false,
  showPermanentlyClosed: false,
  tieredSortOrder: [] as string[],
  defaultImageMode:
    EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.DefaultMissingImage as EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode,
  defaultImageId: null as string | null,
  tierDisplayOverridesByTag: {} as TierDisplayOverridesByTag,
  operatorWidgetFilter:
    EnumOrganizationAppSettingsWidgetsOperatorOperatorWidgetFilter.None as EnumOrganizationAppSettingsWidgetsOperatorOperatorWidgetFilter,
  prioritizeClaimedAndDmoManaged: false,
};

const normalizeId = (id: unknown): string => String(id);

export const useSettingsView = () => {
  const { t } = useTranslation("widgets", { keyPrefix: "SETTINGS_VIEW" });
  const { organizationId: organizationIdFromRoute } = useParams<{
    organizationId?: string;
  }>();
  const { organization } = useOrgHome();
  const loggedInAs = useLoggedInAs();
  const { toastFactory } = useToastContext();
  const [updating, setUpdating] = useState(false);

  const ownerEntityId =
    organizationIdFromRoute ??
    getOrganizationIdFromPathname() ??
    organization?._id ??
    (loggedInAs?.type === EnumOwnerType.Organization
      ? loggedInAs?.entityId
      : undefined);

  const { data: appSettingsData, refetch: refetchAppSettings } =
    useOrganizationAppSettingsWidgetsQuery({
      variables: { id: ownerEntityId! },
      skip: !ownerEntityId,
    });

  const { data: widgetsData } = useWidgetManyQuery({
    variables: {
      filter: {
        owner: {
          entityId: ownerEntityId,
          type: EnumOwnerType.Organization,
        },
      },
      limit: 10000,
      sort: SortFindManyWidgetInput.UpdatedDesc,
    },
    skip: !ownerEntityId,
  });

  const [organizationUpdate] = useOrganizationUpdateMutation();

  const widgets = widgetsData?.widgetMany ?? [];
  const eventWidgets = useMemo(
    () => getFilteredWidgets(widgets, WidgetKey.Event),
    [widgets],
  );
  const operatorWidgets = useMemo(
    () => getFilteredWidgets(widgets, WidgetKey.Operator),
    [widgets],
  );

  const savedHideCancelledEvents = useMemo(() => {
    const ev = appSettingsData?.organizationById?.appSettings?.widgets?.event;
    return ev?.hideCancelledEvents ?? false;
  }, [appSettingsData]);

  const savedOperatorSettings = useMemo((): {
    hideTemporarilyClosed: boolean;
    showPermanentlyClosed: boolean;
    tieredSortOrder: string[];
    defaultImageMode: EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode;
    defaultImageId: string | null;
    tierDisplayOverridesByTag: TierDisplayOverridesByTag;
    operatorWidgetFilter: EnumOrganizationAppSettingsWidgetsOperatorOperatorWidgetFilter;
    prioritizeClaimedAndDmoManaged: boolean;
  } => {
    const op = appSettingsData?.organizationById?.appSettings?.widgets?.operator;
    if (!op) return defaultOperatorSettings;
    const opAny = op as { showPermanentlyClosed?: boolean; hidePermanentlyClosed?: boolean };
    return {
      hideTemporarilyClosed: op.hideTemporarilyClosed ?? false,
      showPermanentlyClosed:
        (opAny.showPermanentlyClosed ?? (opAny.hidePermanentlyClosed === false)) ??
        false,
      tieredSortOrder:
        op.tieredSortOrder
          ?.filter((id): id is NonNullable<typeof id> => id != null)
          .map(id => normalizeId(id)) ?? [],
      defaultImageMode:
        op.defaultImageMode ===
        EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.CustomImage
          ? EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.CustomImage
          : op.defaultImageMode ===
              EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.OrganizationLogo
            ? EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.OrganizationLogo
            : EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.DefaultMissingImage,
      defaultImageId: op.defaultImageId ?? null,
      tierDisplayOverridesByTag: Object.fromEntries(
        (op.tierDisplayOverrides ?? [])
          .filter(override => !!override?.tagId)
          .map(override => [
            normalizeId(override!.tagId),
            {
              statusSortOrder: (override?.statusSortOrder ?? [])
                .filter((id): id is NonNullable<typeof id> => id != null)
                .map(id => normalizeId(id)),
              teaser: {
                hideImage: override?.teaser?.hideImage ?? false,
              },
              slideout: {
                limitImages: override?.slideout?.limitImages ?? null,
                hideAddress: override?.slideout?.hideAddress ?? false,
                hideLongDescription:
                  override?.slideout?.hideLongDescription ?? false,
                hideExternalLinks:
                  override?.slideout?.hideExternalLinks ?? false,
                hideOperatingHours:
                  override?.slideout?.hideOperatingHours ?? false,
                hideMap: override?.slideout?.hideMap ?? false,
              },
            } as TierDisplayOverride,
          ]),
      ),
      operatorWidgetFilter:
        (op as any).operatorWidgetFilter ??
        EnumOrganizationAppSettingsWidgetsOperatorOperatorWidgetFilter.None,
      prioritizeClaimedAndDmoManaged:
        (op as any).prioritizeClaimedAndDmoManaged ?? false,
    };
  }, [appSettingsData]);

  const [localHideCancelledEvents, setLocalHideCancelledEvents] = useState(
    savedHideCancelledEvents,
  );
  const [localHideTemporarilyClosed, setLocalHideTemporarilyClosed] = useState(
    savedOperatorSettings.hideTemporarilyClosed,
  );
  const [localShowPermanentlyClosed, setLocalShowPermanentlyClosed] = useState(
    savedOperatorSettings.showPermanentlyClosed,
  );
  const [localTieredSortOrder, setLocalTieredSortOrder] = useState<string[]>(
    savedOperatorSettings.tieredSortOrder,
  );
  const [localDefaultImageMode, setLocalDefaultImageMode] = useState<
    EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode
  >(savedOperatorSettings.defaultImageMode);
  const [localDefaultImageId, setLocalDefaultImageId] = useState<string | null>(
    savedOperatorSettings.defaultImageId,
  );
  const [localTierDisplayOverridesByTag, setLocalTierDisplayOverridesByTag] =
    useState<TierDisplayOverridesByTag>(
      savedOperatorSettings.tierDisplayOverridesByTag,
    );
  const [localOperatorWidgetFilter, setLocalOperatorWidgetFilter] = useState(
    savedOperatorSettings.operatorWidgetFilter,
  );
  const [localPrioritizeClaimedAndDmoManaged, setLocalPrioritizeClaimedAndDmoManaged] =
    useState(savedOperatorSettings.prioritizeClaimedAndDmoManaged);

  const hasListingFilterFlag = useFeatureFlag(FeatureFlags.OperatorWidgetListingFilter);

  useEffect(() => {
    setLocalHideCancelledEvents(savedHideCancelledEvents);
  }, [savedHideCancelledEvents]);
  useEffect(() => {
    setLocalHideTemporarilyClosed(savedOperatorSettings.hideTemporarilyClosed);
    setLocalShowPermanentlyClosed(savedOperatorSettings.showPermanentlyClosed);
    setLocalTieredSortOrder(savedOperatorSettings.tieredSortOrder);
    setLocalDefaultImageMode(savedOperatorSettings.defaultImageMode);
    setLocalDefaultImageId(savedOperatorSettings.defaultImageId);
    setLocalTierDisplayOverridesByTag(
      savedOperatorSettings.tierDisplayOverridesByTag,
    );
    setLocalOperatorWidgetFilter(savedOperatorSettings.operatorWidgetFilter);
    setLocalPrioritizeClaimedAndDmoManaged(
      savedOperatorSettings.prioritizeClaimedAndDmoManaged,
    );
  }, [savedOperatorSettings]);

  const hasEventChanges = localHideCancelledEvents !== savedHideCancelledEvents;
  const hasOperatorChanges =
    localHideTemporarilyClosed !== savedOperatorSettings.hideTemporarilyClosed ||
    localShowPermanentlyClosed !== savedOperatorSettings.showPermanentlyClosed ||
    JSON.stringify(localTieredSortOrder) !==
      JSON.stringify(savedOperatorSettings.tieredSortOrder) ||
    localDefaultImageMode !== savedOperatorSettings.defaultImageMode ||
    localDefaultImageId !== savedOperatorSettings.defaultImageId ||
    JSON.stringify(localTierDisplayOverridesByTag) !==
      JSON.stringify(savedOperatorSettings.tierDisplayOverridesByTag) ||
    localOperatorWidgetFilter !== savedOperatorSettings.operatorWidgetFilter ||
    localPrioritizeClaimedAndDmoManaged !==
      savedOperatorSettings.prioritizeClaimedAndDmoManaged;
  const hasUnsavedChanges = hasEventChanges || hasOperatorChanges;

  const onSaveSettings = useCallback(
    async () => {
      if (!ownerEntityId) {
        toastFactory.create({
          title: t("TOAST_ERROR_TITLE"),
          description: t("TOAST_ERROR"),
        });
        return;
      }
      if (!hasUnsavedChanges) return;
      setUpdating(true);
      try {
        await organizationUpdate({
          variables: {
            id: ownerEntityId,
            record: {
              appSettings: {
                widgets: {
                  operator: {
                    hideTemporarilyClosed: localHideTemporarilyClosed,
                    showPermanentlyClosed: localShowPermanentlyClosed,
                    tieredSortOrder: localTieredSortOrder,
                    defaultImageMode: localDefaultImageMode,
                    defaultImageId:
                      localDefaultImageMode ===
                      EnumOrganizationAppSettingsWidgetsOperatorDefaultImageMode.CustomImage
                        ? localDefaultImageId
                        : null,
                    operatorWidgetFilter: localOperatorWidgetFilter,
                    prioritizeClaimedAndDmoManaged:
                      localPrioritizeClaimedAndDmoManaged,
                    tierDisplayOverrides: localTieredSortOrder.map(tagId => ({
                      tagId: normalizeId(tagId),
                      statusSortOrder:
                        localTierDisplayOverridesByTag[tagId]?.statusSortOrder ??
                        [],
                      teaser:
                        localTierDisplayOverridesByTag[tagId]?.teaser ?? {
                          hideImage: false,
                        },
                      slideout:
                        localTierDisplayOverridesByTag[tagId]?.slideout ?? {
                          limitImages: null,
                          hideAddress: false,
                          hideLongDescription: false,
                          hideExternalLinks: false,
                          hideOperatingHours: false,
                          hideMap: false,
                        },
                    })),
                  },
                  event: {
                    hideCancelledEvents: localHideCancelledEvents,
                  },
                },
              },
            },
          },
          refetchQueries: [
            { query: WidgetManyDocument },
            {
              query: OrganizationAppSettingsWidgetsDocument,
              variables: { id: ownerEntityId },
            },
          ],
        });
        await refetchAppSettings();
        toastFactory.create({
          title: t("TOAST_SAVED"),
        });
      } catch {
        toastFactory.create({
          title: t("TOAST_ERROR_TITLE"),
          description: t("TOAST_ERROR"),
        });
      } finally {
        setUpdating(false);
      }
    },
    [
      ownerEntityId,
      hasUnsavedChanges,
      localHideTemporarilyClosed,
      localShowPermanentlyClosed,
      localTieredSortOrder,
      localDefaultImageMode,
      localDefaultImageId,
      localTierDisplayOverridesByTag,
      localHideCancelledEvents,
      localOperatorWidgetFilter,
      localPrioritizeClaimedAndDmoManaged,
      organizationUpdate,
      refetchAppSettings,
      toastFactory,
      t,
    ],
  );

  const organizationId =
    organizationIdFromRoute ??
    getOrganizationIdFromPathname() ??
    organization?._id ??
    (loggedInAs?.type === EnumOwnerType.Organization
      ? loggedInAs?.entityId
      : undefined);

  const membershipStatusTagIds = useMemo(
    () =>
      (organization?.metadata?.membershipStatusTagIds ?? [])
        .filter((id): id is NonNullable<typeof id> => id != null)
        .map(id => normalizeId(id)),
    [organization?.metadata?.membershipStatusTagIds],
  );

  return {
    localHideCancelledEvents,
    setLocalHideCancelledEvents,
    localHideTemporarilyClosed,
    setLocalHideTemporarilyClosed,
    localShowPermanentlyClosed,
    setLocalShowPermanentlyClosed,
    localTieredSortOrder,
    setLocalTieredSortOrder,
    localDefaultImageMode,
    setLocalDefaultImageMode,
    localDefaultImageId,
    setLocalDefaultImageId,
    localTierDisplayOverridesByTag,
    setLocalTierDisplayOverridesByTag,
    localOperatorWidgetFilter,
    setLocalOperatorWidgetFilter,
    localPrioritizeClaimedAndDmoManaged,
    setLocalPrioritizeClaimedAndDmoManaged,
    hasListingFilterFlag,
    organizationLogo: appSettingsData?.organizationById?.logo ?? null,
    onSaveSettings,
    updating,
    hasUnsavedChanges,
    eventWidgetsCount: eventWidgets.length,
    operatorWidgetsCount: operatorWidgets.length,
    organizationId,
    membershipStatusTagIds,
  };
};

export type UseSettingsViewReturn = ReturnType<typeof useSettingsView>;
